import React from 'react';
import { PanelMarker, Section } from '../../models';
import { MiniReferenceRangeChart } from '../charts/MiniReferenceRangeChart';

type SystemGroup = {
  systemLabel: string;
  markers: PanelMarker[];
};

type Props = {
  title: string;
  section: Section;
  groups: SystemGroup[];
  // Default true preserves existing (PDF) behavior — inline chart in the row.
  showCharts?: boolean;
  // When true, the chart is rendered below the row instead of inline.
  chartsBelow?: boolean;
};

const zoneClass = (section: Section): string => {
  if (section === Section.NEEDS_ATTENTION) return 'alarm';
  if (section === Section.WORTH_DISCUSSING) return 'lab';
  return 'optimal';
};


const getDirection = (pm: PanelMarker): 'HIGH' | 'LOW' => {
  if (pm.isEGFR?.() || pm.field?.useEGFRChart) return 'LOW';
  if (pm.isGreaterThanAlarm?.() || pm.isGreaterThanLab?.() || pm.isGreaterThanOptimal?.()) return 'HIGH';
  return 'LOW';
};

export const SummaryDashboardSection: React.FC<Props> = ({
  title,
  section,
  groups,
  showCharts = true,
  chartsBelow = false,
}) => {
  const zone = zoneClass(section);
  const hideChartColumn = !showCharts || chartsBelow;
  // When there's no inline chart to fill remaining space, switch the row from
  // a 4-track grid to flex so name/value/badge pack tightly on the left
  // instead of being spread across the full marker-item width.
  const rowStyle: React.CSSProperties | undefined = hideChartColumn
    ? { display: 'flex', alignItems: 'center', gap: 8 }
    : undefined;
  return (
    <div className="summary-dashboard-section">
      {title && <h3>{title}</h3>}
      {groups.map(group => (
        <div key={group.systemLabel} className="summary-system-group">
          <div className="summary-system-header">{group.systemLabel}</div>
          {group.markers.map((pm, i) => {
            const direction = getDirection(pm);
            const label = pm.field?.getChartLabel?.() ?? pm.attribute;
            const value = pm.displayValue?.() ?? '';
            const units = pm.field?.getChartUnits?.() ?? '';
            const renderInlineChart = showCharts && !chartsBelow && pm.range;
            const renderBelowChart = showCharts && chartsBelow && pm.range;
            return (
              <div key={i} className={`summary-marker-item ${zone}`}>
                <div className="summary-marker-row" style={rowStyle}>
                  <span className="summary-marker-name">{label}</span>
                  <span className="summary-marker-value">{value} {units}</span>
                  <span className={`summary-marker-badge ${zone}`}>
                    {direction === 'HIGH' ? '↑ HIGH' : '↓ LOW'}
                  </span>
                  {renderInlineChart && (
                    <div style={{ minWidth: 0, paddingTop: 1 }}>
                      <MiniReferenceRangeChart panelMarker={pm} />
                    </div>
                  )}
                </div>
                {renderBelowChart && (
                  <div style={{ marginTop: 2, paddingTop: 2 }}>
                    <MiniReferenceRangeChart panelMarker={pm} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
